/* eslint-disable react/jsx-no-useless-fragment */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Provider } from 'react-redux';
import {
  BrowserRouter,
  NavigationType,
  Outlet,
  Route,
  Routes,
  useLocation,
  useMatch,
  useNavigationType,
} from 'react-router-dom';
import { Store } from 'redux';
import '../../../stylesheets/animations.sass';
import '../../../stylesheets/reset.sass';
import useCheckAuthStatus from '../../hooks/app/use-check-auth-status';
import useOnAppHydrate from '../../hooks/app/use-on-app-hydrate';
import useOnLocationChanged from '../../hooks/app/use-on-location-changed';
import { useFASTSignupFlowTracker } from '../../hooks/fast/use-fast-signup-flow-tracker';
import useResetScrollEffect from '../../hooks/use-reset-scroll-effect';
import { LOGIN, SUBSCRIBE_NESTED, isValidFooterPath } from '../../utils/router';
import { FlashNotice, FlashNoticesContainer } from '../../web-shared';
import LiveRegionProvider from '../../web-shared/general/live-region-provider';
import { Footer } from '../footer/footer';
import GeoFence from '../geo-fence/geo-fence';
import SuspenseBoundary from '../lazy/suspense-boundary';
import SuspenseLoader from '../lazy/suspense-loader';
import {
  ActivatePage,
  AuthenticatePage,
  AuthorizePage,
  HomePage,
  LaunchPage,
  RegisterPage,
  SignupPage,
} from '../pages/pages';
import UnauthenticatedRoute from '../pages/unauthenticated-route';
import { DynamicPath, Path } from '../../constants/router';

const SubscribePage = () => {
  // KU – check if the pathname is /subscribe only on mount to prevent this
  // component from rendering RegisterPage if the user is on the default landing
  // page and navigates to another lazily loaded route (e.g., /authenticate).
  const { current: useNewLandingPage } = useRef(!!useMatch(SUBSCRIBE_NESTED));

  return useNewLandingPage ? <HomePage /> : <RegisterPage />;
};

function Layout() {
  const navigationType = useNavigationType();
  const { pathname } = useLocation();
  const validFooterPath = useMemo(() => isValidFooterPath(pathname), [pathname]);

  // KU – reset scroll whenever pathname changes, as long as navigationType is
  // not "POP" aka back navigation
  useResetScrollEffect(navigationType !== NavigationType.Pop && `${pathname}${navigationType}`);

  useFASTSignupFlowTracker();

  return (
    <>
      <Outlet />
      <If condition={validFooterPath}>
        <Footer />
      </If>
    </>
  );
}

function AppRoutes() {
  // KU – need to keep these hooks within redux and RR providers
  useOnAppHydrate();
  useCheckAuthStatus();
  useOnLocationChanged();

  // KU – wait for initial mount before rendering app.
  // Should give previous hooks time to run
  const [didMount, setDidMount] = useState(false);
  useEffect(() => setDidMount(true), []);

  return (
    <If condition={didMount}>
      <Routes>
        <Route path="/" element={<Layout />}>
          <Route path={LOGIN}>
            <Route element={<UnauthenticatedRoute />}>
              <Route path={Path.AUTHENTICATE} element={<AuthenticatePage />} />
              <Route path={Path.AUTHORIZE} element={<AuthorizePage />} />
            </Route>
            <Route path={Path.SUBSCRIBE} element={<SubscribePage />} />
            <Route path={Path.GEO_FENCE} element={<GeoFence />} />
            <Route path={Path.SIGNUP} element={<SignupPage />} />
          </Route>
          <Route path={DynamicPath.ACTIVATE} element={<ActivatePage />} />
          <Route path={DynamicPath.GO} element={<LaunchPage />} />
          <Route path={DynamicPath.CANONICAL} element={<LaunchPage />} />
        </Route>
      </Routes>
    </If>
  );
}

export default function App({ store }: { store: Store }) {
  return (
    <LiveRegionProvider>
      <Provider store={store}>
        <FlashNoticesContainer>
          <FlashNotice />
        </FlashNoticesContainer>
        <BrowserRouter>
          <SuspenseBoundary fallbackComponent={SuspenseLoader}>
            <AppRoutes />
          </SuspenseBoundary>
        </BrowserRouter>
      </Provider>
    </LiveRegionProvider>
  );
}
